import React, { useEffect, useState, useCallback } from "react";
import { venueApi, fmtTL, downloadVenueFile } from "@/lib/venueApi";
import { toast } from "sonner";
import { X, FileText, Receipt, Trash2, Pencil, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { inp, SESSIONS, EVENT_TYPES } from "./ReservationForm";

const todayISO = () => new Date().toISOString().slice(0, 10);
const KINDS = { payment: "Ödeme Al", expense: "Gider Ekle", refund: "İade" };

const TONES = {
  slate: ["bg-slate-50 border-slate-100", "text-slate-600", "text-slate-900"],
  rose: ["bg-rose-50 border-rose-100", "text-rose-600", "text-rose-900"],
  emerald: ["bg-emerald-50 border-emerald-100", "text-emerald-600", "text-emerald-900"],
  amber: ["bg-amber-50 border-amber-100", "text-amber-600", "text-amber-900"],
  red: ["bg-red-50 border-red-100", "text-red-600", "text-red-900"],
};
const Stat = ({ label, value, tone = "slate", testid }) => {
  const [box, lab, val] = TONES[tone];
  return (
    <div className={`rounded-2xl p-3 border ${box}`} data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-wider font-medium ${lab}`}>{label}</div>
      <div className={`text-lg font-semibold mt-0.5 ${val}`}>{value}</div>
    </div>
  );
};

const EntryForm = ({ rid, onAdded }) => {
  const [f, setF] = useState({ kind: "payment", amount: "", payment_method: "cash", date: todayISO(), category: "", description: "" });
  const add = async () => {
    if (!Number(f.amount)) { toast.error("Tutar girin"); return; }
    try { await venueApi.post(`/venue/reservations/${rid}/entries`, { ...f, amount: Number(f.amount) }); toast.success("Ön muhasebeye işlendi"); setF({ ...f, amount: "", category: "", description: "" }); onAdded(); }
    catch { toast.error("Eklenemedi"); }
  };
  return (
    <div className="rounded-2xl border border-rose-100 p-4 space-y-3" data-testid="rsv-entry-form">
      <div className="flex gap-1 bg-rose-50 rounded-xl p-1">{Object.entries(KINDS).map(([k, v]) => (
        <button key={k} onClick={() => setF({ ...f, kind: k })} className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${f.kind === k ? "bg-white shadow text-rose-700" : "text-slate-500"}`} data-testid={`rsv-entry-kind-${k}`}>{v}</button>
      ))}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <input type="number" min="0" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="Tutar ₺" className={inp} data-testid="rsv-entry-amount" />
        <select value={f.payment_method} onChange={(e) => setF({ ...f, payment_method: e.target.value })} className={inp} data-testid="rsv-entry-method"><option value="cash">Nakit</option><option value="card">Kart</option><option value="transfer">Havale</option></select>
        <input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inp} data-testid="rsv-entry-date" />
        {f.kind === "expense" && <input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="Kategori (orkestra, çiçek…)" className={inp} data-testid="rsv-entry-category" />}
        <input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Açıklama (opsiyonel)" className={`${inp} ${f.kind === "expense" ? "sm:col-span-2" : "col-span-2 sm:col-span-3"}`} data-testid="rsv-entry-desc" />
      </div>
      <button onClick={add} className="w-full py-2.5 rounded-xl bg-gradient-to-r from-rose-500 to-pink-500 text-white text-sm font-medium shadow" data-testid="rsv-entry-submit">{KINDS[f.kind]} — Kaydet</button>
    </div>
  );
};

const Installments = ({ list, paid }) => {
  let cum = 0; const t = todayISO();
  return (
    <div className="space-y-1.5" data-testid="rsv-detail-installments">
      {list.map((i, idx) => {
        cum += Number(i.amount); const ok = cum <= paid + 0.009, late = !ok && i.due_date < t;
        return (
          <div key={i.id || idx} className={`flex items-center justify-between px-3 py-2 rounded-xl text-sm ${ok ? "bg-emerald-50" : late ? "bg-red-50" : "bg-amber-50"}`}>
            <span className="flex items-center gap-2">{ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : late ? <AlertTriangle className="w-4 h-4 text-red-600" /> : <Clock className="w-4 h-4 text-amber-600" />}{idx + 1}. taksit · {i.due_date}</span>
            <span className="font-medium">{fmtTL(i.amount)}</span>
          </div>
        );
      })}
    </div>
  );
};

export const ReservationDetail = ({ rid, onClose, onChanged, onEdit }) => {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await venueApi.get(`/venue/reservations/${rid}/detail`); setData(data); } catch { toast.error("Yüklenemedi"); }
  }, [rid]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged(); };
  const delTxn = async (id) => { if (!window.confirm("Bu işlem ön muhasebeden silinsin mi?")) return; await venueApi.delete(`/venue/transactions/${id}`); refresh(); };
  if (!data) return null;
  const r = data.reservation;
  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl p-6 space-y-5" onClick={(e) => e.stopPropagation()} data-testid="rsv-detail">
        <div className="flex items-start justify-between gap-3">
          <div><h2 className="text-2xl font-serif text-rose-900" data-testid="rsv-detail-name">{r.couple_name}</h2>
            <div className="text-sm text-slate-500 mt-1">{r.event_date} · {EVENT_TYPES[r.event_type] || r.event_type} · {r.hall || "Salon belirtilmedi"} · {SESSIONS[r.session] || ""}</div>
            <div className="text-xs text-slate-400">{r.package_name || "Paket yok"} · {r.guest_count} kişi · {r.phone}</div></div>
          <button onClick={onClose} data-testid="rsv-detail-close"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => onEdit(r)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-rose-200 text-sm text-rose-700" data-testid="rsv-detail-edit"><Pencil className="w-4 h-4" /> Düzenle</button>
          <button onClick={() => downloadVenueFile(`/venue/reservations/${rid}/contract.pdf`, `sozlesme-${r.couple_name}.pdf`)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-rose-500 text-white text-sm" data-testid="rsv-detail-contract"><FileText className="w-4 h-4" /> Sözleşme PDF</button>
        </div>
        {r.overdue_amount > 0 && <div className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-red-50 text-red-700 text-sm" data-testid="rsv-detail-overdue"><AlertTriangle className="w-4 h-4" /> Geciken ödeme: {fmtTL(r.overdue_amount)}</div>}
        {r.next_due && <div className="text-sm text-amber-700 bg-amber-50 px-4 py-2.5 rounded-2xl" data-testid="rsv-detail-nextdue">Sıradaki vade: {r.next_due.due_date} · {fmtTL(r.next_due.amount)}</div>}
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Toplam" value={fmtTL(r.total_price)} tone="rose" testid="rsv-detail-total" />
          <Stat label="Ödenen" value={fmtTL(r.paid)} tone="emerald" testid="rsv-detail-paid" />
          <Stat label="Kalan" value={fmtTL(r.balance)} tone="amber" testid="rsv-detail-balance" />
          <Stat label="Etkinlik Gideri" value={fmtTL(r.expenses)} tone="slate" testid="rsv-detail-expenses" />
          <Stat label="İade" value={fmtTL(r.refunds)} tone="slate" testid="rsv-detail-refunds" />
          <Stat label="Kâr" value={fmtTL(r.profit)} tone={r.profit >= 0 ? "emerald" : "red"} testid="rsv-detail-profit" />
        </div>
        {(r.installments || []).length > 0 && <Installments list={r.installments} paid={r.paid} />}
        <EntryForm rid={rid} onAdded={refresh} />
        <div>
          <div className="text-sm font-semibold text-rose-900 mb-2">Hesap Hareketleri</div>
          <div className="divide-y divide-rose-50 border border-rose-100 rounded-2xl">
            {data.transactions.length === 0 && <div className="py-8 text-center text-sm text-slate-400">Henüz hareket yok.</div>}
            {data.transactions.map((t) => (
              <div key={t.id} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`rsv-txn-${t.id}`}>
                <div><div className="font-medium text-slate-700">{t.category}</div><div className="text-xs text-slate-400">{t.date} · {t.payment_method === "cash" ? "Nakit" : t.payment_method === "card" ? "Kart" : "Havale"}</div></div>
                <div className="flex items-center gap-2">
                  <span className={`font-semibold ${t.kind === "income" ? "text-emerald-600" : "text-rose-600"}`}>{t.kind === "income" ? "+" : "−"}{fmtTL(t.amount)}</span>
                  <button onClick={() => downloadVenueFile(`/venue/transactions/${t.id}/receipt.pdf`, `makbuz-${t.id.slice(0, 8)}.pdf`)} className="p-1 text-slate-400 hover:text-rose-600" title="Makbuz" data-testid={`rsv-txn-receipt-${t.id}`}><Receipt className="w-4 h-4" /></button>
                  <button onClick={() => delTxn(t.id)} className="p-1 text-slate-400 hover:text-red-600" data-testid={`rsv-txn-del-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
